import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import axios from 'axios';
import { Checkbox } from 'primereact/checkbox';
import { formatDateFromInstant } from 'app/shared/util/date-utils';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload } from 'primereact/fileupload';

const CreateClientRequest: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const fileUploadRef = useRef(null);
  const [image, setImage] = useState(null);
  const [message, setMessage] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (id) {
      axios
        .get(`/api/client-request-message/oneById/${id}?seenBy=1`)
        .then(response => {
          if (response.data) {
            setData(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const onSetResolved = resolved => {
    setLoading(true);

    const da = { resolved };

    axios
      .put(`/api/client-request-message/${id}`, da)
      .then(response => {
        showSuccess('Úspech', 'Data úspešne uložené.');
      })
      .catch(error => {
        console.error('Error creating agent:', error);
        showError('Chyba', error.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const onSubmit = () => {
    const formData = new FormData();

    formData.append('message', message);

    // Append each selected file to formData
    files.forEach(file => {
      formData.append('attachments', file);
    });

    axios
      .post(`/api/client-request-message/sendAnswer/${data.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(r => {
        setMessage('');
        setFiles([]);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        setData(r.data);
        showSuccess('Úspech', 'Správa bola úspešne odoslaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const downloadDocument = async contractDocument => {
    const response = await axios.get(`/api/client-request-message/download/${contractDocument.id}`, {
      responseType: 'blob', // Important for binary data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', contractDocument.originalFilename); // Set the original file name
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const onDownload = rowData => {
    downloadDocument(rowData);
  };

  const actionBodyDocumentsTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {<Button icon="pi pi-download" type="button" rounded outlined className="mr-2" onClick={e => onDownload(rowData)} />}
        </div>
      </React.Fragment>
    );
  };

  const formatName = client => {
    if (client.firstName) {
      if (client.lastName) {
        return client.firstName + ' ' + client.lastName;
      }

      return client.firstName;
    } else {
      return client.businessName;
    }
  };

  const handleSelect = e => {
    // e.files is an array of the newly selected files.
    // If you want to allow multiple selections across multiple picks,
    // you might combine them with existing files in state.
    setFiles(prev => [...prev, ...e.files]);
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && data && (
        <>
          <h2>{`Žiadosť od ${formatName(data.client)}`}</h2>

          <div className="col-12 mb-2">
            <div className="p-field">
              <label htmlFor="resolved">Žiadosť vybavená</label>
              <Checkbox
                name="resolved"
                onChange={e => {
                  setData({ ...data, resolved: e.checked });
                  onSetResolved(e.checked);
                }}
                checked={data.resolved}
              ></Checkbox>
            </div>
          </div>

          <div className="col-12 chat-container">
            {data.messages.map(item => {
              const isCurrentUser = item.isAdminMessage;

              const messageTime = formatDateFromInstant(item.createdDate);

              return (
                <>
                  <div key={item.id} className={`message-bubble ${isCurrentUser ? 'right' : 'left'}`}>
                    <div className="message-user">{isCurrentUser ? item.adminName : formatName(data.client)}</div>
                    <div className="message-text">{item.text}</div>
                    {item.documents && item.documents.length > 0 && (
                      <div className="message-attachments">
                        {item.documents.map(attachment => (
                          <a key={attachment.id} onClick={() => onDownload(attachment)} download className="attachment-link">
                            {attachment.originalFilename}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="message-timestamp">{messageTime}</div>
                  </div>

                  {/*{item.documents.length > 0 && (*/}
                  {/*  <div className="col-6">*/}
                  {/*    <DataTable*/}
                  {/*      value={item.documents}*/}
                  {/*      size="small"*/}
                  {/*      dataKey="id"*/}
                  {/*      emptyMessage="Žiadne prílohy"*/}
                  {/*      sortField="id"*/}
                  {/*      sortOrder={SortOrder.DESC}*/}
                  {/*    >*/}
                  {/*      <Column field="originalFilename" />*/}
                  {/*      <Column field="fileSize" body={v => v?.fileSize / 1000 + ' KB'} />*/}
                  {/*      <Column body={actionBodyDocumentsTemplate} exportable={false} style={{ justifyContent: 'end' }}></Column>*/}
                  {/*    </DataTable>*/}
                  {/*  </div>*/}
                  {/*)}*/}
                </>
              );
            })}
          </div>

          <h5>Odpovedať</h5>
          <InputTextarea
            rows={5}
            autoResize
            style={{ width: '100%' }}
            placeholder="Sem napíšte sprievodný text"
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
          <div className="px-3 pb-3">
            {/* Multiple file upload with "Pridať prílohu" */}
            <FileUpload
              ref={fileUploadRef}
              name="attachments"
              multiple
              accept=".csv,.xml,.pdf,.xlsx,.png,.jpg,.jpeg,.doc,.docx,.xls,.ppt,.pptx,.txt,.odt,.ods,.odp,.ics"
              mode="advanced"
              customUpload
              auto={false} // don't auto-upload
              chooseLabel="Pridať prílohu"
              cancelLabel="Zrušiť"
              uploadLabel={null}
              onSelect={handleSelect}
              chooseOptions={{ icon: 'pi pi-paperclip' }}
            />
          </div>

          <Button type="button" onClick={onSubmit} label="Odoslať odpoveď" icon="pi pi-check" className="p-mt-3" disabled={isSaving} />
          <Button
            type="button"
            style={{ marginTop: '10px' }}
            label="Naspäť"
            icon="pi pi-arrow-left"
            className="p-button-secondary"
            onClick={() => navigate(`/client-requests?p=${tempParameters}`)}
          />
        </>
      )}
    </div>
  );
};

export default CreateClientRequest;
